import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  FormControl,
  FormLabel,
  FormHelperText,
  Switch,
  Textarea,
  Spinner,
  Flex,
  Alert,
  AlertIcon,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  useToast,
  Badge,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { UserContext } from './UserContext';

import UserSettingsLayout from './UserSettingsLayout';
import BrandPreview from './BrandPreview';
import FontPicker from './FontPicker';

const EMPTY_FORM = {
  company_name: '',
  logo_url: '',
  primary_color: '#3B82F6',
  secondary_color: '#10B981',
  page_bg_color: '#FFFFFF',
  card_bg_color: '#F7FAFC',
  heading_color: '#1A202C',
  text_color: '#1A1A1A',
  heading_font: '',
  body_font: '',
  hide_pabs_branding: false,
  custom_footer_text: '',
};

// Colour swatch + hex input
const ColorField = ({ label, value, onChange, helper }) => (
  <FormControl>
    <FormLabel fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.06em" mb={2}>
      {label}
    </FormLabel>
    <HStack spacing={2}>
      <Box
        w="38px"
        h="38px"
        borderRadius="8px"
        border="1px solid"
        borderColor="gray.200"
        bg={value || '#cccccc'}
        overflow="hidden"
        flexShrink={0}
        cursor="pointer"
        position="relative"
        boxShadow="sm"
      >
        <Input
          type="color"
          value={value || '#3B82F6'}
          onChange={(e) => onChange(e.target.value)}
          position="absolute"
          inset={0}
          opacity={0}
          cursor="pointer"
          w="100%"
          h="100%"
          p={0}
          border="none"
        />
      </Box>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#000000"
        fontFamily="mono"
        fontSize="sm"
        size="sm"
        borderRadius="md"
      />
    </HStack>
    {helper && <FormHelperText fontSize="xs">{helper}</FormHelperText>}
  </FormControl>
);

// Section heading for the form
const Section = ({ label, children }) => (
  <Box>
    <Text fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.08em" mb={4}>
      {label}
    </Text>
    {children}
  </Box>
);

const ManualForm = ({ form, onChange }) => (
  <VStack spacing={7} align="stretch">
    <Section label="Identity">
      <Grid templateColumns="1fr 1fr" gap={4}>
        <GridItem colSpan={1}>
          <FormControl>
            <FormLabel fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.06em" mb={2}>
              Company name
            </FormLabel>
            <Input
              value={form.company_name}
              onChange={(e) => onChange('company_name', e.target.value)}
              placeholder="Acme Corp"
              size="sm"
              borderRadius="md"
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={1}>
          <FormControl>
            <FormLabel fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.06em" mb={2}>
              Logo URL
            </FormLabel>
            <Input
              value={form.logo_url}
              onChange={(e) => onChange('logo_url', e.target.value)}
              placeholder="https://yoursite.com/logo.svg"
              fontFamily="mono"
              fontSize="xs"
              size="sm"
              borderRadius="md"
            />
            <FormHelperText fontSize="xs">SVG or PNG. File upload coming soon.</FormHelperText>
          </FormControl>
        </GridItem>
      </Grid>
    </Section>

    <Divider />

    <Section label="Colours">
      <Grid templateColumns="repeat(3, 1fr)" gap={5}>
        <ColorField
          label="Primary"
          value={form.primary_color}
          onChange={(v) => onChange('primary_color', v)}
          helper="Main brand / CTA color"
        />
        <ColorField
          label="Secondary"
          value={form.secondary_color}
          onChange={(v) => onChange('secondary_color', v)}
          helper="Accent color"
        />
        <ColorField
          label="Page background"
          value={form.page_bg_color}
          onChange={(v) => onChange('page_bg_color', v)}
          helper="Main page bg"
        />
        <ColorField
          label="Card background"
          value={form.card_bg_color}
          onChange={(v) => onChange('card_bg_color', v)}
          helper="Cards / sections bg"
        />
        <ColorField
          label="Heading color"
          value={form.heading_color}
          onChange={(v) => onChange('heading_color', v)}
          helper="H1, H2, H3 color"
        />
        <ColorField
          label="Text color"
          value={form.text_color}
          onChange={(v) => onChange('text_color', v)}
          helper="Body text color"
        />
      </Grid>
    </Section>

    <Divider />

    <Section label="Typography">
      <Grid templateColumns="1fr 1fr" gap={4}>
        <FormControl>
          <FormLabel fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.06em" mb={2}>
            Heading font
          </FormLabel>
          <FontPicker
            value={form.heading_font}
            onChange={(v) => onChange('heading_font', v)}
            placeholder="Choose a heading font…"
          />
          <FormHelperText fontSize="xs">Used for titles and headings</FormHelperText>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.06em" mb={2}>
            Body font
          </FormLabel>
          <FontPicker
            value={form.body_font}
            onChange={(v) => onChange('body_font', v)}
            placeholder="Choose a body font…"
          />
          <FormHelperText fontSize="xs">Used for paragraphs and UI text</FormHelperText>
        </FormControl>
      </Grid>
    </Section>

    <Divider />

    <Section label="White-label">
      <VStack spacing={4} align="stretch">
        <FormControl>
          <HStack justify="space-between">
            <Box>
              <Text fontSize="sm" fontWeight="500" color="gray.700">Hide PABS branding</Text>
              <Text fontSize="xs" color="gray.400">Remove PABS logo and footer from shared views</Text>
            </Box>
            <Switch
              isChecked={form.hide_pabs_branding}
              onChange={(e) => onChange('hide_pabs_branding', e.target.checked)}
              colorScheme="brand"
            />
          </HStack>
        </FormControl>

        {form.hide_pabs_branding && (
          <FormControl>
            <FormLabel fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.06em" mb={2}>
              Custom footer text
            </FormLabel>
            <Textarea
              value={form.custom_footer_text}
              onChange={(e) => onChange('custom_footer_text', e.target.value)}
              placeholder="© 2025 Acme Corp. All rights reserved."
              rows={2}
              fontSize="sm"
              size="sm"
              borderRadius="md"
            />
          </FormControl>
        )}
      </VStack>
    </Section>

  </VStack>
);

const Brand = () => {
  const { userDetails } = useContext(UserContext);
  const toast = useToast();
  const { isOpen: isDeleteOpen, onOpen: openDelete, onClose: closeDelete } = useDisclosure();
  const deleteRef = React.useRef();

  const isPro = userDetails?.plan_type === 'pro';

  const [mode, setMode] = useState('loading');
  const [savedBrand, setSavedBrand] = useState(null);
  const [scanUrl, setScanUrl] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [scannedBrand, setScannedBrand] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => { loadBrandSettings(); }, []);

  const loadBrandSettings = async () => {
    try {
      const res = await fetchWithAuth('/api/brand-settings');
      if (res.ok) {
        const data = await res.json();
        if (data) { setSavedBrand(data); setMode('saved'); }
        else setMode('empty');
      } else setMode('empty');
    } catch { setMode('empty'); }
  };

  const handleFieldChange = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleScan = async () => {
    if (!scanUrl.trim()) return;
    setIsScanning(true);
    try {
      const res = await fetchWithAuth('/api/brand/scan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: scanUrl.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Scan failed');
      setScannedBrand(data);
      setForm({
        company_name:       data.company_name || '',
        logo_url:           data.logo_url || '',
        primary_color:      data.primary_color || '#3B82F6',
        secondary_color:    data.secondary_color || '#10B981',
        page_bg_color:      data.page_bg_color || '#FFFFFF',
        card_bg_color:      data.card_bg_color || '#F7FAFC',
        heading_color:      data.heading_color || '#1A202C',
        text_color:         data.text_color || '#1A1A1A',
        heading_font:       data.heading_font || '',
        body_font:          data.body_font || '',
        font_confidence:    data.font_confidence || 'suggested',
        hide_pabs_branding: false,
        custom_footer_text: '',
      });
      setMode('reveal');
    } catch (err) {
      toast({ title: 'Could not scan that URL', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setIsScanning(false);
    }
  };

  const handleSave = async (brandData) => {
    setIsSaving(true);
    try {
      const res = await fetchWithAuth('/api/brand-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(brandData),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error || 'Save failed'); }
      const saved = await res.json();
      setSavedBrand(saved);
      setMode('saved');
      toast({ title: 'Brand saved', status: 'success', duration: 2000 });
    } catch (err) {
      toast({ title: 'Failed to save brand', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const res = await fetchWithAuth('/api/brand-settings', { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      setSavedBrand(null);
      setMode('empty');
      closeDelete();
      toast({ title: 'Brand removed', description: 'Unlinked from all shared views.', status: 'success', duration: 3000 });
    } catch {
      toast({ title: 'Failed to delete brand', status: 'error', duration: 4000 });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditSaved = () => {
    setForm({
      company_name:       savedBrand.company_name || '',
      logo_url:           savedBrand.logo_url || '',
      primary_color:      savedBrand.primary_color || '#3B82F6',
      secondary_color:    savedBrand.secondary_color || '#10B981',
      page_bg_color:      savedBrand.page_bg_color || '#FFFFFF',
      card_bg_color:      savedBrand.card_bg_color || '#F7FAFC',
      heading_color:      savedBrand.heading_color || '#1A202C',
      text_color:         savedBrand.text_color || '#1A1A1A',
      heading_font:       savedBrand.heading_font || '',
      body_font:          savedBrand.body_font || '',
      hide_pabs_branding: savedBrand.hide_pabs_branding || false,
      custom_footer_text: savedBrand.custom_footer_text || '',
    });
    setMode('manual');
  };

  return (
    <UserSettingsLayout>
      <Box px={{ base: 5, md: 10 }} py={8}>

        {/* Page header */}
        <HStack mb={8} justify="space-between" align="center">
          <Box>
            <HStack mb={1} spacing={2}>
              <Text fontSize="xl" fontWeight="700" color="brand.700">Brand</Text>
              <Badge colorScheme="purple" variant="subtle" fontSize="xs">Pro</Badge>
            </HStack>
            <Text fontSize="sm" color="gray.900">
              Apply your brand to shared public plan views.
            </Text>
          </Box>

          {/* Contextual actions — live in the header */}
          <HStack spacing={2}>
            {mode === 'saved' && (
              <>
                <Button
                  size="sm"
                  colorScheme="brand"
                  onClick={handleEditSaved}
                  leftIcon={<Box as="span" className="material-icons-outlined" fontSize="15px">edit</Box>}
                >
                  Edit brand
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  color="red.400"
                  _hover={{ bg: 'red.50', color: 'red.600' }}
                  onClick={openDelete}
                  leftIcon={<Box as="span" className="material-icons-outlined" fontSize="15px">delete_outline</Box>}
                >
                  Remove
                </Button>
              </>
            )}

            {mode === 'reveal' && (
              <>
                <Button
                  size="sm"
                  colorScheme="brand"
                  onClick={() => handleSave(form)}
                  isLoading={isSaving}
                  loadingText="Saving..."
                  leftIcon={<Box as="span" className="material-icons-outlined" fontSize="15px">check</Box>}
                >
                  Save brand
                </Button>
                <Button size="sm" variant="outline" onClick={() => setMode('manual')}
                  leftIcon={<Box as="span" className="material-icons-outlined" fontSize="15px">tune</Box>}>
                  Adjust
                </Button>
                <Button size="sm" variant="ghost" color="gray.400" onClick={() => setMode('scanning')}>
                  Rescan
                </Button>
              </>
            )}

            {mode === 'manual' && (
              <>
                <Button
                  size="sm"
                  colorScheme="brand"
                  onClick={() => handleSave(form)}
                  isLoading={isSaving}
                  loadingText="Saving..."
                  leftIcon={<Box as="span" className="material-icons-outlined" fontSize="15px">check</Box>}
                >
                  Save brand
                </Button>
                <Button size="sm" variant="ghost" color="gray.500"
                  onClick={() => savedBrand ? setMode('saved') : setMode('empty')}>
                  Cancel
                </Button>
              </>
            )}

            {mode === 'scanning' && (
              <Button size="sm" variant="ghost" onClick={() => setMode('empty')}
                leftIcon={<Box as="span" className="material-icons-outlined" fontSize="16px">arrow_back</Box>}>
                Back
              </Button>
            )}
          </HStack>
        </HStack>

        {!isPro && (
          <Alert status="info" borderRadius="lg" mb={6}>
            <AlertIcon />
            <Box>
              <Text fontWeight="600" fontSize="sm">Pro feature</Text>
              <Text fontSize="sm">Upgrade to Pro to apply your brand to shared plan links.</Text>
            </Box>
          </Alert>
        )}

        {mode === 'loading' && (
          <Flex justify="center" py={16}><Spinner size="lg" color="brand.500" /></Flex>
        )}

        {/* Empty — choose entry path */}
        {mode === 'empty' && (
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm" color="gray.500" mb={2}>How would you like to set up your brand?</Text>
            <Grid templateColumns="1fr 1fr" gap={3}>
              <Button
                h="auto"
                py={6}
                variant="outline"
                colorScheme="brand"
                isDisabled={!isPro}
                onClick={() => setMode('scanning')}
                flexDir="column"
                gap={2}
                borderWidth="1.5px"
              >
                <Box as="span" className="material-icons-outlined" fontSize="24px">language</Box>
                <Text fontWeight="600" fontSize="sm">Scan my website</Text>
                <Text fontSize="xs" color="gray.500" fontWeight="400" whiteSpace="normal" textAlign="center">
                  Extract colours, logo, and fonts automatically
                </Text>
              </Button>
              <Button
                h="auto"
                py={6}
                variant="outline"
                colorScheme="gray"
                isDisabled={!isPro}
                onClick={() => { setForm(EMPTY_FORM); setMode('manual'); }}
                flexDir="column"
                gap={2}
                borderWidth="1.5px"
              >
                <Box as="span" className="material-icons-outlined" fontSize="24px">edit</Box>
                <Text fontWeight="600" fontSize="sm">Enter manually</Text>
                <Text fontSize="xs" color="gray.500" fontWeight="400" whiteSpace="normal" textAlign="center">
                  Add your colours, logo, and fonts yourself
                </Text>
              </Button>
            </Grid>
          </VStack>
        )}

        {/* Scan URL input */}
        {mode === 'scanning' && (
          <VStack spacing={5} align="stretch">
            <Box bg="gray.50" borderRadius="xl" p={6} border="1px solid" borderColor="gray.100">
              <Text fontSize="sm" fontWeight="600" color="gray.700" mb={1}>Your website URL</Text>
              <Text fontSize="xs" color="gray.400" mb={4}>We'll use AI to extract your colours, fonts, and logo.</Text>
              <HStack>
                <Input
                  value={scanUrl}
                  onChange={(e) => setScanUrl(e.target.value)}
                  placeholder="acme.com"
                  onKeyDown={(e) => e.key === 'Enter' && handleScan()}
                  bg="white"
                  size="sm"
                  borderRadius="md"
                />
                <Button
                  colorScheme="brand"
                  onClick={handleScan}
                  isLoading={isScanning}
                  loadingText="Analysing..."
                  flexShrink={0}
                  size="sm"
                  rightIcon={<Box as="span" className="material-icons-outlined" fontSize="14px">arrow_forward</Box>}
                >
                  Scan
                </Button>
              </HStack>
            </Box>
            <Button variant="link" colorScheme="gray" size="sm" alignSelf="flex-start"
              onClick={() => { setForm(EMPTY_FORM); setMode('manual'); }}>
              No website? Enter manually →
            </Button>
          </VStack>
        )}

        {/* Post-scan reveal */}
        {mode === 'reveal' && (
          <VStack spacing={4} align="stretch">
            <HStack spacing={2}>
              <Text fontSize="sm" color="gray.500">AI-extracted brand identity</Text>
              {scannedBrand?.confidence && (
                <Badge
                  colorScheme={scannedBrand.confidence === 'high' ? 'green' : scannedBrand.confidence === 'medium' ? 'yellow' : 'orange'}
                  variant="subtle" fontSize="10px"
                >
                  {scannedBrand.confidence} confidence
                </Badge>
              )}
            </HStack>

            {scannedBrand?.note && (
              <Alert status="info" borderRadius="md" fontSize="xs" py={2}>
                <AlertIcon boxSize="14px" />
                <Text>{scannedBrand.note}</Text>
              </Alert>
            )}

            <BrandPreview brand={form} />
          </VStack>
        )}

        {/* Manual form */}
        {mode === 'manual' && (
          <ManualForm form={form} onChange={handleFieldChange} />
        )}

        {/* Saved state */}
        {mode === 'saved' && savedBrand && (
          <BrandPreview brand={savedBrand} />
        )}
      </Box>

      {/* Delete confirm */}
      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={deleteRef} onClose={closeDelete} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="xl">
            <AlertDialogHeader fontSize="md" fontWeight="700" pb={2}>Remove brand?</AlertDialogHeader>
            <AlertDialogBody fontSize="sm" color="gray.600">
              This will remove your brand and unlink it from all shared plan views. This can't be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={deleteRef} onClick={closeDelete} size="sm" variant="ghost">Cancel</Button>
              <Button colorScheme="red" onClick={handleDelete} isLoading={isDeleting} size="sm" ml={3}>
                Remove brand
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </UserSettingsLayout>
  );
};

export default Brand;
